import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import Combobox from '../../app/components/Combobox';
import { fieldChecked, fieldValue, useModalOpen, usePolarisEvents } from '../../app/components/polaris';
import { SafeSaveBar as SaveBar } from '../../app/components/SafeAppBridge';
import ProductThumbnail from '../../app/components/ProductThumbnail';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import { DetectedProductFitment, ProductDetails, ProductFitmentCopyJob, ProductFitmentCopySource, ProductFitmentPage, RealtimeProduct, SaveFitmentChangesPayload, VariantFitmentReview } from '../../app/models/Product';
import { useStore } from '../../app/stores/store';
import { getShopifyStoreHandle } from '../../app/utils/shopifyAdmin';
import { productStatusLabel } from '../../app/utils/productStatus';
import type { FitmentCatalogSettings } from '../../app/models/FitmentCatalog';
import {
    getFitmentFieldEmptyOptionLabel,
    getFitmentFieldValue,
    getLegacyFieldValues,
    getLegacyFieldStateKey,
    getSelectedFitmentLabel,
    getVisibleFitmentFields,
    hasAnyFitmentFieldValue,
    hasRequiredFitmentFieldValues,
    isFitmentFieldDisabled,
    normalizeFitmentFieldValues,
    normalizeFitmentKey,
    updateFitmentFieldValue,
    type FitmentFieldValues,
} from '../../app/utils/fitmentFields';

import { FitmentDraft, fitmentKey, FITMENT_CHANGE_LIMIT } from './FitmentDraft';
import ProductSummaryCard from './ProductSummaryCard';
import { FitmentOptionsCache, useFitmentOptions } from './useFitmentOptions';
import { CopyHistoryModal, RecentCopyActivity } from './ProductCopyActivity';
import { VariantReviewDialog, VariantTargetPicker, variantMissingMatches } from './VariantFitmentDialogs';

type AssignedFitment = ProductDetails['fitments'][number];
type FitmentField = ReturnType<typeof getVisibleFitmentFields>[number];
type AssignedFitmentView = 'grouped' | 'individual';
type AssignedFitmentRow = {
    key: string;
    fitments: AssignedFitment[];
    fieldValues: Record<string, string>;
};
type YearRangeDraft = {
    id: string;
    start: string;
    end: string;
    autoFillEnd?: boolean;
};
type FitmentEditDraft = {
    rowKey: string;
    fitmentKeys: string[];
    fieldValues: Record<string, string>;
    ranges: YearRangeDraft[];
    noteLines: string[];
    focusNotes: boolean;
};
type CopySourceSelection = {
    product: RealtimeProduct;
    source: ProductFitmentCopySource;
};

const productScopeValue = 'product';
const variantScopePrefix = 'variant:';
const assignedFitmentPageSize = 100;

const getVariantScopeValue = (variantId: number) => `${variantScopePrefix}${variantId}`;

const formatFitmentCountLabel = (count: number) => `${count} ${count === 1 ? 'fitment' : 'fitments'}`;

const getFitmentIdentity = (fitment: AssignedFitment) =>
    fitment.version ? 'assignment:' + fitment.id : 'draft:' + fitmentKey(fitment);

const getVariantIdFromScope = (scope: string): number | null => {
    if (!scope.startsWith(variantScopePrefix)) {
        return null;
    }

    const variantId = Number(scope.slice(variantScopePrefix.length));
    return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
};

const sortDetectedFitments = (a: DetectedProductFitment, b: DetectedProductFitment) => {
    const yearCompare = Number(b.year) - Number(a.year);
    if (yearCompare !== 0) {
        return yearCompare;
    }

    const makeCompare = a.make.localeCompare(b.make);
    if (makeCompare !== 0) {
        return makeCompare;
    }

    const modelCompare = a.model.localeCompare(b.model);
    if (modelCompare !== 0) {
        return modelCompare;
    }

    return (a.submodel || '').localeCompare(b.submodel || '');
};

const escapeVehicleTagSegment = (value: string) =>
    value.trim().replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const normalizeFitmentNoteLines = (value: string | string[], preserveBlankLines = false) => {
    const lines = (Array.isArray(value) ? value : value.split(/\r?\n/))
        .map((note) => note.trim());

    return preserveBlankLines ? lines : lines.filter(Boolean);
};

const getFitmentNoteLines = (fitment: ProductDetails['fitments'][number], includeBlankLines = false) =>
    (fitment.notes ?? [])
        .map((note) => note.text ?? '')
        .filter((note) => includeBlankLines || Boolean(note));

const getFitmentNotesText = (fitment: ProductDetails['fitments'][number]) =>
    getFitmentNoteLines(fitment).join('\n');

// The UI edits each note as its own row. Once edited from the PDP, the saved
// notes become Manual.
const buildManualFitmentNotes = (value: string | string[], preserveBlankLines = false) =>
    normalizeFitmentNoteLines(value, preserveBlankLines).map((text, index) => ({
        // Negative IDs only stabilize unsaved React rows; the API persists new IDs.
        id: -(index + 1),
        text,
        source: 'Manual',
        qualifierId: null,
        sortOrder: index + 1,
    }));

type FitmentNotesEditorProps = {
    label: string;
    noteLines: string[];
    disabled?: boolean;
    onChange: (noteLines: string[]) => void;
    autoFocus?: boolean;
};

const FitmentNotesEditor = ({
    label,
    noteLines,
    disabled = false,
    onChange,
    autoFocus = false,
}: FitmentNotesEditorProps) => {
    const rows = noteLines;
    const firstLine = useRef<HTMLElementTagNameMap['s-text-area'] | null>(null);

    useEffect(() => {
        if (autoFocus) firstLine.current?.focus();
    }, [autoFocus]);

    const updateLine = (index: number, value: string) => {
        const nextLines = [...rows];
        nextLines[index] = value;
        onChange(nextLines);
    };

    const removeLine = (index: number) => {
        onChange(rows.filter((_, rowIndex) => rowIndex !== index));
    };

    return (
        <s-stack gap="small-200">
            <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                <s-stack direction="inline" gap="small-200" alignItems="baseline">
                    <s-heading>{label}</s-heading>
                    <s-text color="subdued">Optional</s-text>
                </s-stack>
                <s-button variant="tertiary" icon="plus" onClick={() => onChange([...rows, ''])} disabled={disabled}>Add note</s-button>
            </s-stack>
            {rows.length === 0 && <s-text color="subdued">No notes added.</s-text>}
            {rows.map((value, index) => (
                <s-grid key={`fitment-note-${index}`} gridTemplateColumns="1fr auto" gap="small-200" alignItems="start">
                    <s-text-area
                        ref={index === 0 ? firstLine : undefined}
                        label={`${label} ${index + 1}`}
                        labelAccessibilityVisibility={rows.length === 1 ? 'exclusive' : 'visible'}
                        value={value}
                        onInput={(event) => updateLine(index, fieldValue(event))}
                        onChange={(event) => updateLine(index, fieldValue(event))}
                        disabled={disabled}
                        placeholder={`Note ${index + 1}`}
                        rows={2}
                    />
                    <s-button
                        variant="tertiary"
                        tone="critical"
                        icon="delete"
                        onClick={() => removeLine(index)}
                        disabled={disabled}
                        accessibilityLabel={`Remove note ${index + 1}`}
                    />
                </s-grid>
            ))}
        </s-stack>
    );
};

const buildStableClientTagId = (tag: string) =>
    Math.abs(tag.split('').reduce((hash, character) => ((hash * 31) + character.charCodeAt(0)) | 0, 17));

const buildFitmentTagFromValues = (values: FitmentFieldValues, useYmmPrefix: boolean) => {
    const legacyValues = getLegacyFieldValues(values);
    const legacyTagSegments = [legacyValues.make, legacyValues.model, legacyValues.submodel, legacyValues.year]
        .filter(Boolean)
        .map(escapeVehicleTagSegment);
    const baseTag = legacyTagSegments.join('-');

    return baseTag ? (useYmmPrefix ? `ymm_${baseTag}` : baseTag) : '';
};

const buildDraftFitmentFromValues = (
    values: FitmentFieldValues,
    fields: FitmentField[],
    useYmmPrefix: boolean,
    notes: AssignedFitment['notes'] = [],
    sourceType = 'Manual',
    preserveOtherFields = false,
): AssignedFitment => {
    const normalizedValues = preserveOtherFields
        ? Object.fromEntries(Object.entries(values).filter(([, value]) => value.trim()).map(([key, value]) => [key, value.trim()]))
        : normalizeFitmentFieldValues(fields, values);
    const legacyValues = getLegacyFieldValues(normalizedValues);
    const fitmentKey = JSON.stringify(normalizedValues);
    const tag = buildFitmentTagFromValues(normalizedValues, useYmmPrefix);

    return {
        id: buildStableClientTagId(tag || fitmentKey),
        tag,
        year: legacyValues.year,
        make: legacyValues.make,
        model: legacyValues.model,
        submodel: legacyValues.submodel,
        fieldValues: normalizedValues,
        sourceType,
        source: tag || getSelectedFitmentLabel(fields, normalizedValues),
        canRemove: true,
        createdAt: new Date().toISOString(),
        notes: (notes ?? []).map((note) => ({ ...note })),
    };
};

const getProductFitmentFieldValue = (
    fitment: ProductDetails['fitments'][number] | DetectedProductFitment,
    key: ReturnType<typeof getLegacyFieldStateKey>,
) => {
    if (key === 'year') return fitment.year;
    if (key === 'make') return fitment.make;
    if (key === 'model') return fitment.model;
    if (key === 'submodel') return fitment.submodel || '-';
    return '-';
};

const getFitmentDisplayValue = (
    fitment: ProductDetails['fitments'][number] | DetectedProductFitment,
    field: FitmentField,
) => {
    const key = normalizeFitmentKey(field);
    // Prefer generic fieldValues. The legacy columns are only a fallback for
    // Shopify-tag-derived rows and older product fitment responses.
    if ('fieldValues' in fitment && fitment.fieldValues?.[key]) {
        return fitment.fieldValues[key];
    }

    return getProductFitmentFieldValue(fitment, getLegacyFieldStateKey(field));
};

const parseYearValue = (value: string): number | null => {
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
};

const isCanonicalVehicleYear = (value: string) => /^(?:19|20)\d{2}$/.test(value.trim());
const canonicalVehicleYearError = 'Vehicle years must be between 1900 and 2099.';

const getNextYearRangeStart = (ranges: YearRangeDraft[]) => {
    const years = ranges.flatMap((range) => [range.start, range.end])
        .filter(isCanonicalVehicleYear)
        .map(Number);
    const nextYear = years.length > 0 ? Math.max(...years) + 1 : new Date().getFullYear();
    return String(Math.min(2099, Math.max(1900, nextYear)));
};

const compareYearDescending = (leftValue: string, rightValue: string) => {
    const leftYear = parseYearValue(leftValue);
    const rightYear = parseYearValue(rightValue);

    if (leftYear != null && rightYear != null && leftYear !== rightYear) {
        return rightYear - leftYear;
    }

    if (leftYear != null && rightYear == null) {
        return -1;
    }

    if (leftYear == null && rightYear != null) {
        return 1;
    }

    return rightValue.localeCompare(leftValue, undefined, { numeric: true, sensitivity: 'base' });
};

const compareYearAscending = (leftValue: string, rightValue: string) =>
    -compareYearDescending(leftValue, rightValue);

const compareYearByConfiguredDirection = (
    leftValue: string,
    rightValue: string,
    yearField?: FitmentField,
) =>
    yearField?.sortDirection === 'Asc'
        ? compareYearAscending(leftValue, rightValue)
        : compareYearDescending(leftValue, rightValue);

const compareFitmentValueAscending = (leftValue: string, rightValue: string) =>
    leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: 'base' });

const getFitmentNotesSignature = (fitment: AssignedFitment) =>
    (fitment.notes ?? [])
        .map((note) => [
            note.text,
            note.source,
            note.qualifierId ?? '',
            note.sortOrder,
        ].join('\u001f'))
        .join('\u001e');

const isStandardFitmentField = (field: FitmentField, key: string) =>
    (field.key || '').trim().toLowerCase() === key;

const orderFitmentFieldsForPdp = (fields: FitmentField[], yearField?: FitmentField) => {
    if (!yearField) {
        return fields;
    }

    const makeField = fields.find((field) => isStandardFitmentField(field, 'make'));
    const modelField = fields.find((field) => isStandardFitmentField(field, 'model'));
    const prioritizedFields = [makeField, modelField].filter((field): field is FitmentField => !!field);
    const prioritizedSet = new Set(prioritizedFields);

    return [
        ...prioritizedFields,
        ...fields.filter((field) => field !== yearField && !prioritizedSet.has(field)),
        yearField,
    ];
};

const sortAssignedFitments = (
    fitments: AssignedFitment[],
    fields: FitmentField[],
    yearField?: FitmentField,
) =>
    [...fitments].sort((left, right) => {
        for (const field of fields.filter((field) => field !== yearField)) {
            const comparison = compareFitmentValueAscending(
                getFitmentDisplayValue(left, field),
                getFitmentDisplayValue(right, field),
            );
            if (comparison !== 0) {
                return comparison;
            }
        }

        if (yearField) {
            const yearComparison = compareYearByConfiguredDirection(
                getFitmentDisplayValue(left, yearField),
                getFitmentDisplayValue(right, yearField),
                yearField,
            );
            if (yearComparison !== 0) {
                return yearComparison;
            }
        }

        return getFitmentIdentity(left).localeCompare(getFitmentIdentity(right), undefined, { numeric: true });
    });

const formatYearRange = (values: string[]) => {
    const uniqueValues = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
    if (uniqueValues.length === 0) {
        return '';
    }

    const numericYears = uniqueValues
        .filter((value) => /^\d+$/.test(value))
        .map((value) => Number(value))
        .sort((left, right) => left - right);
    const nonNumericYears = uniqueValues
        .filter((value) => !/^\d+$/.test(value))
        .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));

    const ranges: string[] = [];
    if (numericYears.length > 0) {
        let rangeStart = numericYears[0];
        let previousYear = numericYears[0];

        numericYears.slice(1).forEach((year) => {
            if (year === previousYear + 1) {
                previousYear = year;
                return;
            }

            ranges.push(rangeStart === previousYear ? `${rangeStart}` : `${rangeStart}-${previousYear}`);
            rangeStart = year;
            previousYear = year;
        });

        ranges.push(rangeStart === previousYear ? `${rangeStart}` : `${rangeStart}-${previousYear}`);
    }

    return [...ranges, ...nonNumericYears].join(', ');
};

const buildYearRangeDrafts = (values: string[]): YearRangeDraft[] => {
    const uniqueValues = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
    const numericYears = uniqueValues
        .filter((value) => /^\d+$/.test(value))
        .map((value) => Number(value))
        .sort((left, right) => left - right);
    const nonNumericYears = uniqueValues
        .filter((value) => !/^\d+$/.test(value))
        .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
    const ranges: YearRangeDraft[] = [];

    if (numericYears.length > 0) {
        let rangeStart = numericYears[0];
        let previousYear = numericYears[0];

        numericYears.slice(1).forEach((year) => {
            if (year === previousYear + 1) {
                previousYear = year;
                return;
            }

            ranges.push({
                id: `${rangeStart}-${previousYear}-${ranges.length}`,
                start: `${rangeStart}`,
                end: `${previousYear}`,
            });
            rangeStart = year;
            previousYear = year;
        });

        ranges.push({
            id: `${rangeStart}-${previousYear}-${ranges.length}`,
            start: `${rangeStart}`,
            end: `${previousYear}`,
        });
    }

    nonNumericYears.forEach((year) => {
        ranges.push({
            id: `${year}-${ranges.length}`,
            start: year,
            end: year,
        });
    });

    return ranges.length > 0 ? ranges : [{ id: 'range-0', start: '', end: '', autoFillEnd: true }];
};

const expandYearRangeDrafts = (ranges: YearRangeDraft[]) => {
    const years: string[] = [];
    const seenYears = new Set<string>();

    for (const range of ranges) {
        const start = range.start.trim();
        const end = range.end.trim();
        if (!start || !end) {
            return { years: [], error: 'Enter a start and end year for each range.' };
        }

        const startYear = parseYearValue(start);
        const endYear = parseYearValue(end);
        if (startYear == null || endYear == null) {
            return { years: [], error: 'Year ranges must use whole-number years.' };
        }

        if (!isCanonicalVehicleYear(start) || !isCanonicalVehicleYear(end)) {
            return { years: [], error: canonicalVehicleYearError };
        }

        if (startYear > endYear) {
            return { years: [], error: 'Start year must be before or equal to end year.' };
        }

        for (let year = startYear; year <= endYear; year += 1) {
            const yearValue = `${year}`;
            if (seenYears.has(yearValue)) {
                return { years: [], error: 'Year ranges cannot overlap.' };
            }

            seenYears.add(yearValue);
            years.push(yearValue);
        }
    }

    return { years, error: '' };
};

const buildIndividualYearFitmentRows = (
    fitments: AssignedFitment[],
    fields: FitmentField[],
): AssignedFitmentRow[] =>
    fitments.map((fitment) => ({
        key: getFitmentIdentity(fitment),
        fitments: [fitment],
        fieldValues: fields.reduce<Record<string, string>>((values, field) => {
            values[normalizeFitmentKey(field)] = getFitmentDisplayValue(fitment, field);
            return values;
        }, {}),
    }));

const buildGroupedFitmentRows = (
    fitments: AssignedFitment[],
    fields: FitmentField[],
    yearField: FitmentField,
): AssignedFitmentRow[] => {
    const groupedRows = new Map<string, AssignedFitment[]>();
    fitments.forEach((fitment) => {
        const groupKey = [
            // Hidden custom values also distinguish assignments; never merge
            // different vehicles merely because those fields aren't displayed.
            ...Object.entries(fitment.fieldValues ?? {}).filter(([key]) => key !== 'year')
                .sort(([a], [b]) => a.localeCompare(b)).map(([key, value]) => `${key}=${value}`),
            `notes=${getFitmentNotesSignature(fitment)}`,
        ].join('\u001e');

        const group = groupedRows.get(groupKey) ?? [];
        group.push(fitment);
        groupedRows.set(groupKey, group);
    });

    return Array.from(groupedRows.values()).map((group) => {
        const firstFitment = group[0];
        const fieldValues = fields.reduce<Record<string, string>>((values, field) => {
            const key = normalizeFitmentKey(field);
            values[key] = key === 'year'
                ? formatYearRange(group.map((fitment) => getFitmentDisplayValue(fitment, yearField)))
                : getFitmentDisplayValue(firstFitment, field);
            return values;
        }, {});

        return {
            key: group.length === 1
                ? getFitmentIdentity(firstFitment)
                : `group:${group.map(getFitmentIdentity).join('|')}`,
            fitments: group,
            fieldValues,
        };
    });
};

const getAddFitmentYearValues = (startValue: string, endValue: string) => {
    const start = startValue.trim();
    const end = endValue.trim();

    if (!start && !end) {
        return { values: [''], error: '' };
    }

    if (!start) {
        return { values: [], error: 'Choose a starting year before choosing Year To.' };
    }

    const startYear = parseYearValue(start);
    if (startYear == null) {
        return { values: [], error: 'Year ranges must use whole-number years.' };
    }

    if (!isCanonicalVehicleYear(start)) {
        return { values: [], error: canonicalVehicleYearError };
    }

    if (!end || end === start) {
        return { values: [start], error: '' };
    }

    const endYear = parseYearValue(end);
    if (endYear == null) {
        return { values: [], error: 'Year ranges must use whole-number years.' };
    }

    if (!isCanonicalVehicleYear(end)) {
        return { values: [], error: canonicalVehicleYearError };
    }

    if (startYear > endYear) {
        return { values: [], error: 'Year To must be the same as or after the starting year.' };
    }

    return {
        values: Array.from({ length: endYear - startYear + 1 }, (_, index) => `${startYear + index}`),
        error: '',
    };
};

const ProductDetailsPage: React.FC = () => {
    const { id } = useParams();
    const productId = Number(id);
    const navigate = useNavigate();
    const { userStore } = useStore();
    const [fitmentCatalog, setFitmentCatalog] = useState<FitmentCatalogSettings | null>(null);
    const [catalogLoaded, setCatalogLoaded] = useState(false);

    const [product, setProduct] = useState<ProductDetails | null>(null);
    const [editDraft, setEditDraft] = useState(() => new FitmentDraft());
    const editDraftRef = useRef(editDraft);
    const receiveDraft = useCallback((next: FitmentDraft) => { editDraftRef.current = next; setEditDraft(next); }, []);
    const draftFitments = useMemo(() => editDraft.visible(), [editDraft]);
    const [reviewChanges, setReviewChanges] = useState(false);
    const [syncRecoveryId, setSyncRecoveryId] = useState<string | null>(null);
    const [saveAttempt, setSaveAttempt] = useState<Parameters<typeof agent.Products.saveFitmentChanges>[1] | null>(null);
    const [targetVariantIds, setTargetVariantIds] = useState<number[]>([]);
    const [variantPickerOpen, setVariantPickerOpen] = useState(false);
    const [variantReview, setVariantReview] = useState<{ review: VariantFitmentReview; attempt: SaveFitmentChangesPayload } | null>(null);
    const setDraftFitments = useCallback((update: React.SetStateAction<ProductDetails['fitments']>) => {
        try {
            const current = editDraftRef.current;
            receiveDraft(current.stage(typeof update === 'function' ? update(current.visible()) : update));
            return true;
        } catch (e) { setError((e as Error).message); return false; }
    }, [receiveDraft]);
    const scopeKeyRef = useRef('');
    const pageRequestRef = useRef(0);
    const detailsRequestRef = useRef(0);
    const detailsAbortRef = useRef<AbortController | undefined>(undefined);
    const resolvedScopeRef = useRef<{ productId: number; variantId: number | null } | undefined>(undefined);
    const pageAbortRef = useRef<AbortController | undefined>(undefined);
    const [detailsReady, setDetailsReady] = useState(false);
    const [detailsRevision, setDetailsRevision] = useState(0);
    const [fitmentPageReady, setFitmentPageReady] = useState(false);
    const [scopeAction, setScopeAction] = useState<{ operation: string; count: number; version: string } | null>(null);
    const [draftIsUniversal, setDraftIsUniversal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [assignedFitmentPageLoading, setAssignedFitmentPageLoading] = useState(false);
    const [assignedFitmentPageError, setAssignedFitmentPageError] = useState('');
    const requestedFitmentPageRef = useRef(1);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>('');
    const [useYmmPrefixForProductTags, setUseYmmPrefixForProductTags] = useState(true);
    const [showRawTags, setShowRawTags] = useState(false);
    const [copyQuery, setCopyQuery] = useState('');
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copySourceProducts, setCopySourceProducts] = useState<RealtimeProduct[]>([]);
    const [copySearching, setCopySearching] = useState(false);
    const [includeCopiedFitmentNotes, setIncludeCopiedFitmentNotes] = useState(true);
    const [copySourceSelection, setCopySourceSelection] = useState<CopySourceSelection | null>(null);
    const [copyJobs, setCopyJobs] = useState<ProductFitmentCopyJob[]>([]);
    const [copyHistoryOpen, setCopyHistoryOpen] = useState(false);
    const copyJobStatesRef = useRef<Map<number, string>>(new Map());
    const [assignedFitmentView, setAssignedFitmentView] = useState<AssignedFitmentView>('grouped');
    const [assignedFitmentSearch, setAssignedFitmentSearch] = useState('');
    const [debouncedFitmentSearch, setDebouncedFitmentSearch] = useState('');
    const [editingFitment, setEditingFitment] = useState<FitmentEditDraft | null>(null);
    const fitmentNotesRef = useRef<HTMLElementTagNameMap['s-box']>(null);
    const [fitmentEditError, setFitmentEditError] = useState('');
    const [editOptionsDelay, setEditOptionsDelay] = useState(0);

    const [selectedFitmentValues, setSelectedFitmentValues] = useState<FitmentFieldValues>({});
    const [selectedFitmentYearTo, setSelectedFitmentYearTo] = useState('');
    const [newFitmentNoteLines, setNewFitmentNoteLines] = useState<string[]>([]);
    const [showNewFitmentNotes, setShowNewFitmentNotes] = useState(false);
    const [optionsDelay, setOptionsDelay] = useState(0);
    const [optionsRevision, setOptionsRevision] = useState(0);
    // These dependencies invalidate page-owned options when their scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const optionsCache = useMemo(() => new FitmentOptionsCache(), [productId, userStore.user?.shopDomain, fitmentCatalog, optionsRevision]);
    const activeCopyJob = copyJobs.find((job) => job.isActive);
    const copyJobRunning = !!activeCopyJob;
    const visibleFitmentFields = useMemo(() => getVisibleFitmentFields(fitmentCatalog), [fitmentCatalog]);
    const visibleFitmentFieldKey = useMemo(
        () => visibleFitmentFields.map((field) => normalizeFitmentKey(field)).join('|'),
        [visibleFitmentFields],
    );
    const standardYearFitmentField = useMemo(
        () => visibleFitmentFields.find((field) => isStandardFitmentField(field, 'year')),
        [visibleFitmentFields],
    );
    const productDetailsFitmentFields = useMemo(
        () => orderFitmentFieldsForPdp(visibleFitmentFields, standardYearFitmentField),
        [standardYearFitmentField, visibleFitmentFields],
    );
    const editableFitmentFields = useMemo(
        () => productDetailsFitmentFields.filter((field) => field !== standardYearFitmentField),
        [productDetailsFitmentFields, standardYearFitmentField],
    );
    const editingFitmentValues = editingFitment?.fieldValues;
    const { options: fieldOptions, loading: loadingFieldOptions } = useFitmentOptions(
        optionsCache, productDetailsFitmentFields, selectedFitmentValues, catalogLoaded, optionsDelay);
    const { options: editFieldOptions, loading: loadingEditFieldOptions } = useFitmentOptions(
        optionsCache, productDetailsFitmentFields, editingFitmentValues, catalogLoaded, editOptionsDelay, editableFitmentFields);
    // The Add button follows the merchant's required-field setup. In a custom
    // catalog with no required fields, one selected value is enough to add.
    const hasVehicleContext = Boolean(
        visibleFitmentFields.length > 0
        && (visibleFitmentFields.some((field) => field.isRequired)
            ? hasRequiredFitmentFieldValues(visibleFitmentFields, selectedFitmentValues)
            : hasAnyFitmentFieldValue(visibleFitmentFields, selectedFitmentValues))
    );

    const applyFitmentPage = useCallback((result: ProductFitmentPage) => {
        setProduct((current) => current
            ? {
                ...current,
                fitmentCount: result.fitmentCount,
                totalRows: result.totalRows,
                fitmentLoadLimit: result.fitmentLoadLimit,
                fitmentsTruncated: result.fitmentsTruncated,
                fitmentPage: result.fitmentPage,
                fitmentPageSize: result.fitmentPageSize,
                fitmentHasNext: result.fitmentHasNext,
                fitmentHasPrevious: result.fitmentHasPrevious,
                fitments: result.fitments || [],
            }
            : current);
        receiveDraft(editDraftRef.current.receive(result.fitments || []));
        setEditingFitment(null);
        setFitmentEditError('');
    }, [receiveDraft]);

    const loadedProductId = product?.id;
    const loadedVariantId = product?.selectedVariantId ?? 0;
    const loadAssignedFitmentPage = useCallback(async (page: number) => {
        if (!detailsReady || !catalogLoaded || loadedProductId !== productId) return;
        const request = ++pageRequestRef.current;
        pageAbortRef.current?.abort();
        const controller = new AbortController();
        pageAbortRef.current = controller;
        requestedFitmentPageRef.current = page;
        setAssignedFitmentPageError('');
        setAssignedFitmentPageLoading(true);
        setFitmentPageReady(false);
        try {
            const result = await agent.Products.fitments(loadedProductId, loadedVariantId,
                page, assignedFitmentPageSize, debouncedFitmentSearch, !!standardYearFitmentField && assignedFitmentView === 'grouped', controller.signal);
            if (request === pageRequestRef.current) {
                applyFitmentPage(result);
                setFitmentPageReady(true);
            }
        } catch (e: any) {
            if (request === pageRequestRef.current && e?.code !== 'ERR_CANCELED') {
                setAssignedFitmentPageError('Assigned fitments could not be loaded. Please retry.');
            }
        } finally {
            if (request === pageRequestRef.current) setAssignedFitmentPageLoading(false);
        }
    }, [applyFitmentPage, loadedProductId, loadedVariantId, productId, detailsReady, catalogLoaded, debouncedFitmentSearch, standardYearFitmentField, assignedFitmentView]);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedFitmentSearch(assignedFitmentSearch), 250);
        return () => window.clearTimeout(timer);
    }, [assignedFitmentSearch]);

    useEffect(() => {
        const sequence = pageRequestRef;
        void loadAssignedFitmentPage(1);
        return () => { sequence.current++; pageAbortRef.current?.abort(); };
    }, [loadAssignedFitmentPage, detailsRevision]);

    const loadDetails = useCallback(async (requestedScope?: string) => {
        if (!productId || Number.isNaN(productId)) {
            setError('Invalid product id.');
            return;
        }

        const request = ++detailsRequestRef.current;
        detailsAbortRef.current?.abort();
        const controller = new AbortController();
        detailsAbortRef.current = controller;
        ++pageRequestRef.current;
        pageAbortRef.current?.abort();
        setLoading(true);
        setDetailsReady(false);
        setFitmentPageReady(false);
        setAssignedFitmentPageLoading(false);
        setAssignedFitmentPageError('');
        setError('');
        try {
            const requestedVariantId = requestedScope === productScopeValue
                ? 0
                : requestedScope !== undefined ? getVariantIdFromScope(requestedScope)
                    : resolvedScopeRef.current?.productId === productId ? resolvedScopeRef.current.variantId ?? 0 : undefined;
            // Resolve the scope once and load only the header here. The table
            // effect owns page loading; neither automatic scope selection nor
            // table navigation should repeat the Shopify product refresh.
            const result = await agent.Products.details(productId, requestedVariantId, 1, assignedFitmentPageSize, true, false, controller.signal);
            if (request !== detailsRequestRef.current) return;
            // Details can import a previously uncached Shopify product. Check
            // recovery only after that refresh, so authorization sees the row.
            const pending = await agent.Products.pendingFitmentSave(productId);
            if (request !== detailsRequestRef.current) return;
            setSyncRecoveryId(pending.id);
            setProduct(result);
            const scopeKey = String(productId) + ':' + String(result.selectedVariantId ?? 0);
            if (scopeKeyRef.current !== scopeKey) {
                setTargetVariantIds([]);
                setVariantReview(null);
                setVariantPickerOpen(false);
            }
            receiveDraft(scopeKeyRef.current === scopeKey ? editDraftRef.current.receive([]) : new FitmentDraft());
            scopeKeyRef.current = scopeKey;
            resolvedScopeRef.current = { productId, variantId: result.selectedVariantId ?? null };
            setEditingFitment(null);
            setFitmentEditError('');
            setDraftIsUniversal(result.selectedVariantId ? false : !!result.isUniversal);
            setDetailsReady(true);
            setDetailsRevision((revision) => revision + 1);
        } catch (e: any) {
            if (request === detailsRequestRef.current && e?.code !== 'ERR_CANCELED') setError(e?.message ?? 'Failed to load product details.');
        } finally {
            if (request === detailsRequestRef.current) setLoading(false);
        }
    }, [productId, receiveDraft]);

    useEffect(() => {
        loadDetails();
        const sequence = detailsRequestRef;
        return () => { sequence.current++; detailsAbortRef.current?.abort(); };
    }, [loadDetails]);

    const loadCopyJobs = useCallback(async (signal: AbortSignal) => {
        if (!productId || Number.isNaN(productId)) return [] as ProductFitmentCopyJob[];

        try {
            const nextJobs = await agent.Products.listFitmentCopyJobs(productId, 10, { recentOnly: true, signal });
            if (signal.aborted) return [];
            const completedWhileOpen = nextJobs.some((job) => {
                const previousState = copyJobStatesRef.current.get(job.id);
                return (previousState === 'Queued' || previousState === 'Processing')
                    && (job.state === 'Completed' || job.state === 'CompletedWithWarnings');
            }) || Array.from(copyJobStatesRef.current).some(([id, state]) =>
                (state === 'Queued' || state === 'Processing') && !nextJobs.some((job) => job.id === id));

            copyJobStatesRef.current = new Map(nextJobs.map((job) => [job.id, job.state]));
            setCopyJobs(nextJobs);
            if (completedWhileOpen) {
                setOptionsRevision((revision) => revision + 1);
                await loadDetails();
            }
            return nextJobs;
        } catch {
            // Copy actions surface errors directly. Polling failures remain quiet
            // so a temporary network issue does not replace the product content.
            return [] as ProductFitmentCopyJob[];
        }
    }, [loadDetails, productId]);

    useEffect(() => {
        let disposed = false;
        let timeoutId: number | undefined;
        const controller = new AbortController();
        setCopyJobs([]);
        setCopyHistoryOpen(false);
        copyJobStatesRef.current.clear();

        const poll = async () => {
            const jobs = await loadCopyJobs(controller.signal);
            if (disposed) return;
            timeoutId = window.setTimeout(poll, jobs.some((job) => job.isActive) ? 2500 : 10000);
        };

        poll();
        return () => {
            disposed = true;
            controller.abort();
            if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        };
    }, [loadCopyJobs]);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settings = await agent.Settings.details();
                setUseYmmPrefixForProductTags(settings.useYmmPrefixForProductTags ?? true);
            } catch {
                setUseYmmPrefixForProductTags(true);
            }
        };

        loadSettings();
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadFitmentCatalog = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (!cancelled) setFitmentCatalog(catalog);
            } catch {
                if (!cancelled) setFitmentCatalog(null);
            } finally {
                if (!cancelled) setCatalogLoaded(true);
            }
        };

        loadFitmentCatalog();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setSelectedFitmentValues((current) => normalizeFitmentFieldValues(visibleFitmentFields, current));
    }, [visibleFitmentFieldKey, visibleFitmentFields]);

    const selectedFitmentYearValue = standardYearFitmentField
        ? getFitmentFieldValue(selectedFitmentValues, standardYearFitmentField)
        : '';
    useEffect(() => {
        // Year to is separate from the catalog fields, but must reset with the
        // starting year when an earlier vehicle selection changes or is cleared.
        if (!selectedFitmentYearValue.trim()) setSelectedFitmentYearTo('');
    }, [selectedFitmentYearValue]);

    const addFitmentYearRange = useMemo(
        () => standardYearFitmentField
            ? getAddFitmentYearValues(selectedFitmentYearValue, selectedFitmentYearTo)
            : { values: [''], error: '' },
        [selectedFitmentYearTo, selectedFitmentYearValue, standardYearFitmentField],
    );
    const canAddFitment = hasVehicleContext && !addFitmentYearRange.error;

    const onAddFitment = useCallback(async () => {
        if (!canAddFitment) {
            return;
        }

        const normalizedValues = normalizeFitmentFieldValues(visibleFitmentFields, selectedFitmentValues);
        const yearKey = standardYearFitmentField ? normalizeFitmentKey(standardYearFitmentField) : '';
        const notes = buildManualFitmentNotes(newFitmentNoteLines);
        const yearValues = standardYearFitmentField
            ? addFitmentYearRange.values
            : [''];
        const fitmentsToAdd = yearValues.map((yearValue) => {
            const values = yearKey
                ? normalizeFitmentFieldValues(visibleFitmentFields, { ...normalizedValues, [yearKey]: yearValue })
                : normalizedValues;

            return buildDraftFitmentFromValues(values, visibleFitmentFields, useYmmPrefixForProductTags, notes);
        });

        const staged = setDraftFitments((currentFitments) => {
            const existingFieldValueKeys = new Set(currentFitments.map((fitment) => JSON.stringify(fitment.fieldValues ?? {})));
            const newFitments = fitmentsToAdd.filter((fitment) => {
                const key = JSON.stringify(fitment.fieldValues ?? {});
                if (existingFieldValueKeys.has(key)) {
                    return false;
                }

                existingFieldValueKeys.add(key);
                return true;
            });

            return newFitments.length > 0 ? [...currentFitments, ...newFitments] : currentFitments;
        });

        if (!staged) return;
        setSelectedFitmentValues({});
        setSelectedFitmentYearTo('');
        setNewFitmentNoteLines([]);
        setShowNewFitmentNotes(false);
    }, [setDraftFitments, addFitmentYearRange.values, canAddFitment, newFitmentNoteLines, selectedFitmentValues, standardYearFitmentField, useYmmPrefixForProductTags, visibleFitmentFields]);

    const onRemoveFitments = useCallback((fitmentKeys: string[], rowKey: string) => {
        const fitmentKeySet = new Set(fitmentKeys);
        setDraftFitments((currentFitments) =>
            currentFitments.filter((fitment) => !fitmentKeySet.has(getFitmentIdentity(fitment))));
        setEditingFitment((current) => current?.rowKey === rowKey ? null : current);
    }, [setDraftFitments]);

    const onOpenFitmentEditor = useCallback((row: AssignedFitmentRow, focusNotes = false) => {
        setEditOptionsDelay(0);
        const noteLines = getFitmentNoteLines(row.fitments[0]);
        setEditingFitment({
            rowKey: row.key,
            fitmentKeys: row.fitments.map(getFitmentIdentity),
            fieldValues: normalizeFitmentFieldValues(editableFitmentFields, row.fieldValues),
            ranges: standardYearFitmentField
                ? buildYearRangeDrafts(row.fitments.map((fitment) => getFitmentDisplayValue(fitment, standardYearFitmentField)))
                : [],
            noteLines: noteLines.length > 0 ? noteLines : [''],
            focusNotes,
        });
        setFitmentEditError('');
    }, [editableFitmentFields, standardYearFitmentField]);

    const onUpdateEditedFitmentField = useCallback((field: FitmentField, value: string, immediate: boolean) => {
        setEditOptionsDelay(immediate ? 0 : 200);
        setEditingFitment((current) => current && getFitmentFieldValue(current.fieldValues, field) !== value ? {
            ...current,
            fieldValues: updateFitmentFieldValue(editableFitmentFields, current.fieldValues, field, value),
        } : current);
        setFitmentEditError('');
    }, [editableFitmentFields]);

    const onUpdateYearGroupRange = useCallback((rangeId: string, field: 'start' | 'end', value: string, fromSpinner = false) => {
        setEditingFitment((current) => current
            ? {
                ...current,
                ranges: current.ranges.map((range) => {
                    if (range.id !== rangeId) return range;

                    const pairedYear = field === 'start' ? range.end : range.start;
                    const nextValue = fromSpinner && !isCanonicalVehicleYear(range[field])
                        ? isCanonicalVehicleYear(pairedYear)
                            ? pairedYear.trim()
                            : getNextYearRangeStart(current.ranges)
                        : value;

                    if (field === 'end') {
                        return { ...range, end: nextValue, autoFillEnd: false };
                    }

                    return {
                        ...range,
                        start: nextValue,
                        end: range.autoFillEnd && isCanonicalVehicleYear(nextValue) ? nextValue.trim() : range.end,
                    };
                }),
            }
            : current);
        setFitmentEditError('');
    }, []);

    // Arrow keys step a valid year; from an empty or partial year they start
    // after the latest range, matching the add-fitment year defaults.
    const stepYearRange = (event: React.KeyboardEvent, range: YearRangeDraft, field: 'start' | 'end') => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        event.preventDefault();
        const current = range[field];
        const next = isCanonicalVehicleYear(current)
            ? String(Math.min(2099, Math.max(1900, Number(current) + (event.key === 'ArrowUp' ? 1 : -1))))
            : '';
        onUpdateYearGroupRange(range.id, field, next, true);
    };

    const onAddYearGroupRange = useCallback(() => {
        setEditingFitment((current) => current
            ? {
                ...current,
                ranges: [
                    ...current.ranges,
                    { id: `range-${Date.now()}`, start: '', end: '', autoFillEnd: true },
                ],
            }
            : current);
        setFitmentEditError('');
    }, []);

    const onRemoveYearGroupRange = useCallback((rangeId: string) => {
        setEditingFitment((current) => current
            ? {
                ...current,
                ranges: current.ranges.length > 1
                    ? current.ranges.filter((range) => range.id !== rangeId)
                    : current.ranges,
            }
            : current);
        setFitmentEditError('');
    }, []);

    const onApplyFitmentEdit = useCallback(() => {
        if (!editingFitment) {
            return;
        }

        const missingFields = editableFitmentFields.filter((field) => field.isRequired && !getFitmentFieldValue(editingFitment.fieldValues, field).trim());
        if (missingFields.length > 0) {
            setFitmentEditError(`Enter the required fields: ${missingFields.map((field) => field.label).join(', ')}.`);
            return;
        }
        const oversizedField = editableFitmentFields.find((field) => getFitmentFieldValue(editingFitment.fieldValues, field).trim().length > Math.min(field.maxLength || 200, 200));
        if (oversizedField) {
            setFitmentEditError(`${oversizedField.label} must be at most ${Math.min(oversizedField.maxLength || 200, 200)} characters.`);
            return;
        }

        const optionalYearIsEmpty = standardYearFitmentField && !standardYearFitmentField.isRequired
            && editingFitment.ranges.length === 1 && !editingFitment.ranges[0].start.trim() && !editingFitment.ranges[0].end.trim();
        const result = standardYearFitmentField && !optionalYearIsEmpty ? expandYearRangeDrafts(editingFitment.ranges) : { years: [''], error: '' };
        if (result.error) {
            setFitmentEditError(result.error);
            return;
        }

        if (result.years.length === 0) {
            setFitmentEditError('Add at least one year range.');
            return;
        }

        const fitmentKeySet = new Set(editingFitment.fitmentKeys);
        const currentDraft = editDraftRef.current;
        const currentFitments = currentDraft.visible();
        const originalFitments = currentFitments.filter((fitment) => fitmentKeySet.has(getFitmentIdentity(fitment)));
        if (originalFitments.length !== fitmentKeySet.size) {
            setFitmentEditError('This row changed while you were editing. Close the editor and open it again.');
            return;
        }
        // Grouped rows share the complete note signature. Only an explicit note
        // change replaces imported metadata; untouched notes stay on each year.
        const notesChanged = JSON.stringify(normalizeFitmentNoteLines(editingFitment.noteLines))
            !== JSON.stringify(normalizeFitmentNoteLines(getFitmentNoteLines(originalFitments[0])));
        const editedNotes = notesChanged ? buildManualFitmentNotes(editingFitment.noteLines) : undefined;
        const replacementFitments = result.years.map((year) => {
            const original = originalFitments.find((fitment) => (fitment.fieldValues?.year ?? fitment.year) === year) ?? originalFitments[0];
            const values = { ...(original.fieldValues ?? { year: original.year, make: original.make, model: original.model, submodel: original.submodel }) };
            editableFitmentFields.forEach((field) => {
                const key = normalizeFitmentKey(field);
                const value = getFitmentFieldValue(editingFitment.fieldValues, field).trim();
                if (value) values[key] = value;
                else delete values[key];
            });
            if (standardYearFitmentField) values.year = year;
            const replacement = buildDraftFitmentFromValues(values, visibleFitmentFields, useYmmPrefixForProductTags, editedNotes ?? original.notes, 'Manual', true);
            const existing = originalFitments.find((fitment) => fitmentKey(fitment) === fitmentKey(replacement));
            return existing ? editedNotes ? { ...existing, notes: editedNotes } : existing : replacement;
        });
        if (replacementFitments.some((fitment) => !hasAnyFitmentFieldValue(visibleFitmentFields, fitment.fieldValues ?? {}))) {
            setFitmentEditError('Enter at least one fitment value.');
            return;
        }
        const remainingFitments = currentFitments.filter((fitment) => !fitmentKeySet.has(getFitmentIdentity(fitment)));
        const existingKeys = new Set([
            ...remainingFitments.map(fitmentKey),
            ...Array.from(currentDraft.changes.values()).flatMap((change) => change.after && !fitmentKeySet.has(getFitmentIdentity(change.after)) ? [fitmentKey(change.after)] : []),
        ]);
        if (replacementFitments.some((fitment) => existingKeys.has(fitmentKey(fitment)))) {
            setFitmentEditError('This fitment is already assigned on another row. Edit that row or choose different values.');
            return;
        }

        try {
            receiveDraft(currentDraft.replace(originalFitments, replacementFitments, [...remainingFitments, ...replacementFitments]));
        } catch (e) {
            setFitmentEditError((e as Error).message);
            return;
        }
        setEditingFitment(null);
        setFitmentEditError('');
    }, [editableFitmentFields, editingFitment, receiveDraft, standardYearFitmentField, useYmmPrefixForProductTags, visibleFitmentFields]);

    const onDiscardChanges = useCallback(() => {
        receiveDraft(new FitmentDraft(product?.fitments || []));
        setTargetVariantIds([]);
        setVariantReview(null);
        setDraftIsUniversal(product?.selectedVariantId ? false : !!product?.isUniversal);
        setEditingFitment(null);
        setFitmentEditError('');
        setError('');
    }, [receiveDraft, product?.fitments, product?.isUniversal, product?.selectedVariantId]);

    const prepareScopeAction = useCallback(async (operation: string) => {
        if (editDraftRef.current.changes.size > 0 || saveAttempt || syncRecoveryId) {
            setError('Save or discard your pending changes before this action.'); return;
        }
        setSubmitting(true);
        try {
            const summary = await agent.Products.fitmentEditSummary(productId, product?.selectedVariantId);
            setScopeAction({ operation, ...summary });
        } catch (e) { setError((e as Error).message); }
        finally { setSubmitting(false); }
    }, [productId, product?.selectedVariantId, saveAttempt, syncRecoveryId]);
    const onToggleUniversal = (checked: boolean) => prepareScopeAction(checked ? 'universal' : 'specific');

    const onSearchCopySource = useCallback(async () => {
        if (!copyQuery.trim()) {
            setCopySourceProducts([]);
            return;
        }

        setCopySearching(true);
        setError('');
        try {
            const response = await agent.Products.listRealtime(copyQuery.trim(), '', 20);
            setCopySourceProducts(
                response.products.filter((candidate: RealtimeProduct) => candidate.id !== productId),
            );
        } catch (e: any) {
            setError(e?.message ?? 'Failed to search products.');
            setCopySourceProducts([]);
        } finally {
            setCopySearching(false);
        }
    }, [copyQuery, productId]);

    useEffect(() => {
        if (!copyModalOpen) {
            return;
        }

        if (!copyQuery.trim()) {
            setCopySourceProducts([]);
            setCopySearching(false);
            return;
        }

        // Debounce the modal search so typing feels responsive without firing a
        // realtime Shopify query on every keypress.
        const timeoutId = window.setTimeout(() => {
            onSearchCopySource();
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [copyModalOpen, copyQuery, onSearchCopySource]);

    const onOpenCopyModal = useCallback(() => {
        setIncludeCopiedFitmentNotes(true);
        setCopySourceSelection(null);
        setCopyModalOpen(true);
    }, []);

    const onCloseCopyModal = useCallback(() => {
        setCopyModalOpen(false);
        setCopySourceSelection(null);
    }, []);

    const onReviewCopySource = useCallback(async (sourceProduct: RealtimeProduct) => {
        setSubmitting(true);
        setError('');
        try {
            // Copy from the whole source product, including assignments stored
            // on its variants. The API de-duplicates identical vehicle records
            // and omits the inline payload when the background worker is needed.
            const source = await agent.Products.fitmentCopySource(sourceProduct.id);
            if (source.isUniversal && source.fitmentCount === 0) {
                setError('This universal product has no explicit fitments to copy.');
                return;
            }
            setCopySourceSelection({ product: sourceProduct, source });
        } catch (e: any) {
            setError(e?.message ?? 'Failed to load fitments from the selected product.');
        } finally {
            setSubmitting(false);
        }
    }, []);

    const onConfirmCopyFitments = useCallback(async () => {
        if (!copySourceSelection) {
            return;
        }

        const { product: sourceProductDetails } = copySourceSelection;
        setSubmitting(true);
        setError('');
        try {
            const job = await agent.Products.createFitmentCopyJob(productId, sourceProductDetails.id, product?.selectedVariantId ?? null, includeCopiedFitmentNotes);
            setCopyJobs(current => [job, ...current.filter(item => item.id !== job.id)].slice(0, 10));
            copyJobStatesRef.current.set(job.id, job.state);
            onCloseCopyModal();
        } catch (e: any) {
            setError(e?.message ?? 'Failed to copy fitments from the selected product.');
        } finally {
            setSubmitting(false);
        }
    }, [copySourceSelection, includeCopiedFitmentNotes, onCloseCopyModal, product, productId]);

    const onRetryCopyJob = useCallback(async (job: ProductFitmentCopyJob) => {
        setSubmitting(true);
        setError('');
        try {
            const retriedJob = await agent.Products.retryFitmentCopyJob(productId, job.id);
            setCopyJobs((current) => [retriedJob, ...current.filter((item) => item.id !== job.id)].slice(0, 10));
            copyJobStatesRef.current.set(retriedJob.id, retriedJob.state);
            return retriedJob;
        } catch (e: any) {
            setError(e?.message ?? 'Failed to retry the fitment copy.');
        } finally {
            setSubmitting(false);
        }
    }, [productId]);

    const submitChanges = useCallback(async (attempt: Parameters<typeof agent.Products.saveFitmentChanges>[1]) => {
        setSubmitting(true);
        setSaveAttempt(attempt);
        setError('');
        try {
            const result = await agent.Products.saveFitmentChanges(productId, attempt);
            setOptionsRevision((revision) => revision + 1);
            if (result.syncPending) {
                setSaveAttempt(null);
                receiveDraft(new FitmentDraft());
                setSyncRecoveryId(attempt.mutationId);
                setError(result.message);
                setScopeAction(null);
                await loadDetails();
                return;
            }
            setSaveAttempt(null);
            setScopeAction(null);
            setVariantReview(null);
            setTargetVariantIds([]);
            receiveDraft(new FitmentDraft());
            await loadDetails();
        } catch (e: any) {
            if (e?.response?.status === 400 || e?.response?.status === 409 || e?.status === 400 || e?.status === 409) {
                setSaveAttempt(null);
                setVariantReview(null);
            }
            setError(await getApiErrorMessage(e, 'Save could not be confirmed. Retry Save; your changes are retained.'));
        } finally { setSubmitting(false); }
    }, [productId, receiveDraft, loadDetails]);
    const checkAndSaveVariantChanges = async (attempt: SaveFitmentChangesPayload) => {
        setVariantReview(null);
        if (!attempt.targetVariantIds?.length) return submitChanges(attempt);
        setSubmitting(true);
        setError('');
        try {
            const review = await agent.Products.previewVariantFitmentChanges(productId, attempt);
            const checkedAttempt = { ...attempt, reviewVersion: review.version };
            if (!review.canSave || review.variants.some(variant => variantMissingMatches(variant) > 0)) {
                setVariantReview({ review, attempt: checkedAttempt });
            } else {
                await submitChanges(checkedAttempt);
            }
        } catch (e) { setError(await getApiErrorMessage(e, 'Could not check the changes. Your draft is retained.')); }
        finally { setSubmitting(false); }
    };
    const excludeConflictingVariants = async (ids: number[]) => {
        if (!variantReview || submitting || saveAttempt) return;
        const remaining = (variantReview.attempt.targetVariantIds ?? []).filter(id => !ids.includes(id));
        setTargetVariantIds(remaining);
        await checkAndSaveVariantChanges({ ...variantReview.attempt, targetVariantIds: remaining,
            mutationId: crypto.randomUUID(), reviewVersion: undefined });
    };
    const onSaveChanges = async () => {
        if (submitting || variantReview || targetVariantIds.length > 100) return;
        setVariantPickerOpen(false);
        if (syncRecoveryId) {
            setSubmitting(true);
            try {
                const result = await agent.Products.retryFitmentSave(productId, syncRecoveryId);
                if (result.syncPending) setError(result.message);
                else { setSyncRecoveryId(null); setError(''); await loadDetails(); }
            } catch (e) { setError(await getApiErrorMessage(e, 'Shopify synchronization could not finish. Retry Save.')); }
            finally { setSubmitting(false); }
            return;
        }
        if (saveAttempt) return submitChanges(saveAttempt);
        const attempt: SaveFitmentChangesPayload = {
            mutationId: crypto.randomUUID(), variantId: product?.selectedVariantId,
            changes: editDraftRef.current.payload(targetVariantIds.length > 0),
            ...(targetVariantIds.length ? { targetVariantIds } : {}),
        };
        return checkAndSaveVariantChanges(attempt);
    };

    const onImportDetectedFitments = useCallback(() => {
        if (!product?.detectedFitments?.length) {
            return;
        }

        setDraftFitments((currentFitments) => {
            const currentTags = new Set(currentFitments.map((fitment) => fitment.tag.toLowerCase()));
            const importedFitments = product.detectedFitments
                .filter((fitment) => !currentTags.has(fitment.canonicalTag.toLowerCase()))
                .map((fitment) => ({
                    id: buildStableClientTagId(fitment.canonicalTag),
                    tag: fitment.canonicalTag,
                    year: fitment.year,
                    make: fitment.make,
                    model: fitment.model,
                    submodel: fitment.submodel,
                    fieldValues: {
                        year: fitment.year,
                        make: fitment.make,
                        model: fitment.model,
                        submodel: fitment.submodel,
                    },
                    sourceType: 'Detected Tag',
                    source: fitment.sourceTag,
                    canRemove: true,
                    createdAt: new Date().toISOString(),
                    notes: [],
                }));

            return [...currentFitments, ...importedFitments];
        });
    }, [product?.detectedFitments, setDraftFitments]);

    const filteredFitments = draftFitments;
    const sortedFitments = useMemo(
        () => sortAssignedFitments(filteredFitments, productDetailsFitmentFields, standardYearFitmentField),
        [filteredFitments, productDetailsFitmentFields, standardYearFitmentField],
    );
    const effectiveAssignedFitmentView: AssignedFitmentView = standardYearFitmentField ? assignedFitmentView : 'individual';
    const assignedFitmentRows = useMemo(
        () => effectiveAssignedFitmentView === 'grouped' && standardYearFitmentField
            ? buildGroupedFitmentRows(sortedFitments, productDetailsFitmentFields, standardYearFitmentField)
            : buildIndividualYearFitmentRows(sortedFitments, productDetailsFitmentFields),
        [effectiveAssignedFitmentView, sortedFitments, productDetailsFitmentFields, standardYearFitmentField],
    );
    const pendingFitmentRows = useMemo(() => {
        // Use the full draft, including changes on other pages and removed rows.
        // Reuse table grouping so notes and hidden vehicle values stay distinct.
        const fitments = sortAssignedFitments(
            Array.from(editDraft.changes.values(), (change) => (change.after ?? change.before)!),
            productDetailsFitmentFields, standardYearFitmentField,
        );
        return effectiveAssignedFitmentView === 'grouped' && standardYearFitmentField
            ? buildGroupedFitmentRows(fitments, productDetailsFitmentFields, standardYearFitmentField)
            : buildIndividualYearFitmentRows(fitments, productDetailsFitmentFields);
    }, [editDraft.changes, effectiveAssignedFitmentView, productDetailsFitmentFields, standardYearFitmentField]);
    const pendingFitmentSummary = `${pendingFitmentRows.length.toLocaleString()} ${pendingFitmentRows.length === 1 ? 'row' : 'rows'} edited · ${formatFitmentCountLabel(editDraft.changes.size)} affected`;
    const isAssignedFitmentsLoading = loading || assignedFitmentPageLoading || ((!product || !catalogLoaded || !fitmentPageReady) && !error && !assignedFitmentPageError);
    const hasAssignedFitmentRefinements = assignedFitmentSearch.trim().length > 0;
    // Keep search available after an empty result, including while clearing a
    // debounced query. A genuinely empty product needs no table toolbar.
    const showAssignedFitmentToolbar = (product?.fitmentCount ?? 0) > 0 || draftFitments.length > 0
        || hasAssignedFitmentRefinements || debouncedFitmentSearch.trim().length > 0;
    const draftTagSet = new Set(draftFitments.map((fitment) => fitment.tag.toLowerCase()));
    const selectedVariantId = product?.selectedVariantId ?? null;
    const isVariantScope = !!selectedVariantId;
    const selectedVariant = product?.variants?.find((variant) => variant.id === selectedVariantId);
    const copyTargetScopeLabel = selectedVariant
        ? `${selectedVariant.sku ? `SKU ${selectedVariant.sku}` : selectedVariant.title || `variant ${selectedVariant.id}`} variant`
        : 'product-level fitment scope';
    const fitmentsTruncated = !!product?.fitmentsTruncated;
    const copyTargetFitmentCount = fitmentsTruncated
        ? (product?.fitmentCount ?? draftFitments.length)
        : draftFitments.length;
    const copyRunsInBackground = !!copySourceSelection;
    const assignedFitmentCurrentPage = product?.fitmentPage ?? 1;
    const assignedFitmentEffectivePageSize = product?.fitmentPageSize || assignedFitmentPageSize;
    const assignedFitmentTotalPages = Math.max(1, Math.ceil((product?.totalRows ?? product?.fitmentCount ?? 0) / assignedFitmentEffectivePageSize));
    const variantsWithFitments = (product?.variants ?? []).filter((variant) => variant.fitmentCount > 0);
    const showFitmentScopeSelector = (product?.variants?.length ?? 0) > 1
        || ((product?.productLevelFitmentCount ?? 0) > 0 && variantsWithFitments.length > 0);
    const importableDetectedFitments = (product?.detectedFitments || [])
        .filter((fitment) => !draftTagSet.has(fitment.canonicalTag.toLowerCase()))
        .sort(sortDetectedFitments);
    const hasUnsavedChanges = editDraft.changes.size > 0 || !!saveAttempt || !!syncRecoveryId;
    const editingBlocked = submitting || loading || !detailsReady || !fitmentPageReady || assignedFitmentPageLoading || copyJobRunning || !!saveAttempt || !!syncRecoveryId;
    const blocker = useBlocker(hasUnsavedChanges);
    useEffect(() => {
        if (!hasUnsavedChanges) return;
        const preventLeave = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', preventLeave);
        return () => window.removeEventListener('beforeunload', preventLeave);
    }, [hasUnsavedChanges]);
    const latestFailedCopyJob = !activeCopyJob
        ? copyJobs.find((job) => job.state === 'Failed')
        : undefined;
    const fitmentScopeOptions = [
        {
            label: `Product-level fitments (${formatFitmentCountLabel(product?.productLevelFitmentCount ?? 0)})`,
            value: productScopeValue,
        },
        { title: 'Variants', options: (product?.variants ?? []).map((variant) => {
            let label = `Variant ${variant.id}`;
            if (variant.sku && variant.title) {
                label = `${variant.sku} — ${variant.title}`;
            } else if (variant.title) {
                label = variant.title;
            } else if (variant.sku) {
                label = `SKU ${variant.sku}`;
            }

            label = `${label} (${formatFitmentCountLabel(variant.fitmentCount)})`;

            return {
                label,
                value: getVariantScopeValue(variant.id),
            };
        }) },
    ];
    const selectedScopeValue = selectedVariantId ? getVariantScopeValue(selectedVariantId) : productScopeValue;
    const shopHandle = getShopifyStoreHandle(userStore.user?.shopDomain);
    const shopifyAdminProductUrl = shopHandle && product?.id
        ? `https://admin.shopify.com/store/${shopHandle}/products/${product.id}`
        : '';
    const fitmentSelectorConfigured = visibleFitmentFields.length > 0;
    const closeFitmentEditor = () => {
        setEditingFitment(null);
        setFitmentEditError('');
    };
    const copyModalRef = useModalOpen(copyModalOpen, onCloseCopyModal);
    const scopeModalRef = useModalOpen(!!scopeAction, () => { if (!submitting && !saveAttempt) setScopeAction(null); });
    const blockerModalRef = useModalOpen(blocker.state === 'blocked', () => { if (blocker.state === 'blocked') blocker.reset(); });
    const reviewModalRef = useModalOpen(reviewChanges, () => setReviewChanges(false));
    const editModalRef = useModalOpen(!!editingFitment, closeFitmentEditor, {
        aftershow: () => {
            if (editingFitment?.focusNotes) fitmentNotesRef.current?.querySelector<HTMLElement>('s-text-area')?.focus();
        },
    });
    const assignedTableEvents = usePolarisEvents({
        previouspage: () => {
            if (!assignedFitmentPageLoading && product?.fitmentHasPrevious) void loadAssignedFitmentPage(Math.max(1, assignedFitmentCurrentPage - 1));
        },
        nextpage: () => {
            if (!assignedFitmentPageLoading && product?.fitmentHasNext) void loadAssignedFitmentPage(assignedFitmentCurrentPage + 1);
        },
    });
    const fitmentActionsMenuId = 'pf-fitment-actions-menu';
    // One row of fields on wide screens; written out because
    // Polaris container values cannot contain commas inside repeat().
    const addFitmentFieldCount = productDetailsFitmentFields.length + (standardYearFitmentField ? 1 : 0);
    const addFitmentGridColumns = `@container (inline-size > ${Math.max(560, addFitmentFieldCount * 130)}px) ${'1fr '.repeat(addFitmentFieldCount).trim()}, 1fr 1fr`;

    return (
        <>
            <SaveBar id="product-fitments-save-bar" open={hasUnsavedChanges}>
                <button onClick={onDiscardChanges} disabled={submitting || loading || copyJobRunning || !!saveAttempt || !!syncRecoveryId}>
                    Discard
                </button>
                <button variant="primary" onClick={onSaveChanges} disabled={submitting || loading || copyJobRunning || !!variantReview || targetVariantIds.length > 100} loading={submitting}>
                    {targetVariantIds.length ? `Save to ${targetVariantIds.length + 1} variants` : 'Save'}
                </button>
            </SaveBar>

            <s-page heading={product?.title || 'Product Details'} inlineSize="large">
                <s-link slot="breadcrumb-actions" href="/products" onClick={(event) => {
                    event.preventDefault();
                    navigate('/products');
                }}>
                    Products
                </s-link>

                <CopyHistoryModal key={productId} productId={productId} open={copyHistoryOpen} onClose={() => setCopyHistoryOpen(false)}
                    jobs={copyJobs} variants={product?.variants} onRetry={onRetryCopyJob}
                    disabled={submitting || loading || copyJobRunning || hasUnsavedChanges} />

                <s-modal
                    ref={copyModalRef}
                    id="pf-copy-fitments-modal"
                    size="large"
                    heading={copySourceSelection ? 'Review fitment replacement' : 'Copy fitments from another product'}
                >
                    {copySourceSelection ? (
                        <s-stack gap="base">
                            <s-banner
                                heading={copyTargetFitmentCount > 0
                                    ? `Replace ${formatFitmentCountLabel(copyTargetFitmentCount)}?`
                                    : `Copy ${formatFitmentCountLabel(copySourceSelection.source.fitmentCount)}?`}
                                tone={copyTargetFitmentCount > 0 ? 'warning' : 'info'}
                            >
                                {copyTargetFitmentCount > 0 ? (
                                    <>
                                        The current list for {copyTargetScopeLabel} will be removed and replaced with{' '}
                                        <s-text fontWeight="semibold">{formatFitmentCountLabel(copySourceSelection.source.fitmentCount)}</s-text>{' '}
                                        from &quot;{copySourceSelection.product.title}&quot;
                                        {copySourceSelection.product.sku ? ` (SKU ${copySourceSelection.product.sku})` : ''}.
                                    </>
                                ) : (
                                    <>
                                        The <s-text fontWeight="semibold">{formatFitmentCountLabel(copySourceSelection.source.fitmentCount)}</s-text>{' '}
                                        from &quot;{copySourceSelection.product.title}&quot;
                                        {copySourceSelection.product.sku ? ` (SKU ${copySourceSelection.product.sku})` : ''}{' '}
                                        will become the complete list for {copyTargetScopeLabel}.
                                    </>
                                )}
                            </s-banner>

                            <s-paragraph>
                                {includeCopiedFitmentNotes
                                    ? 'Fitment notes from the source product will also be copied.'
                                    : 'Fitment notes from the source product will not be copied.'}
                            </s-paragraph>

                            <s-paragraph color="subdued">
                                {copyRunsInBackground
                                    ? 'This replacement starts immediately and continues in the background. It cannot be discarded from the Save bar.'
                                    : 'The replacement will be added to this page’s draft. You can review it before clicking Save.'}
                            </s-paragraph>
                        </s-stack>
                    ) : (
                        <s-stack gap="base">
                            <s-banner
                                heading={copyTargetFitmentCount > 0
                                    ? 'Existing fitments will be replaced'
                                    : 'The source becomes the complete fitment list'}
                                tone={copyTargetFitmentCount > 0 ? 'warning' : 'info'}
                            >
                                Copying fitments does not merge two lists.{' '}
                                {copyTargetFitmentCount > 0 ? (
                                    <>
                                        All <s-text fontWeight="semibold">{formatFitmentCountLabel(copyTargetFitmentCount)}</s-text> assigned to{' '}
                                        {copyTargetScopeLabel} will be removed and replaced with fitments from the selected product.
                                    </>
                                ) : (
                                    <>
                                        Fitments from the selected product will become the complete list for {copyTargetScopeLabel}.
                                    </>
                                )}
                            </s-banner>

                            <s-search-field
                                label="Search products"
                                value={copyQuery}
                                onInput={(event) => setCopyQuery(fieldValue(event))}
                                placeholder="Search by title, SKU, or vendor"
                            />

                            <s-checkbox
                                label="Include fitment notes"
                                checked={includeCopiedFitmentNotes}
                                onChange={(event) => setIncludeCopiedFitmentNotes(fieldChecked(event))}
                                disabled={submitting || loading || copyJobRunning}
                            />
                            <s-text color="subdued">
                                After confirmation, the replacement runs in the background. You can leave this page while it finishes.
                            </s-text>

                            <s-stack gap="small-200">
                                {copySourceProducts.map((candidate) => (
                                    <s-clickable
                                        key={candidate.id}
                                        onClick={() => onReviewCopySource(candidate)}
                                        disabled={submitting || loading || copyJobRunning}
                                        border="base"
                                        borderRadius="base"
                                        padding="small"
                                        accessibilityLabel={`Review ${candidate.title}`}
                                    >
                                        <s-grid gridTemplateColumns="auto 1fr auto auto" gap="small" alignItems="center">
                                            <ProductThumbnail source={candidate.featuredImage} alt={candidate.title} size="small" />
                                            <s-stack gap="none">
                                                <s-text fontWeight="medium">{candidate.title}</s-text>
                                                <s-text color="subdued">SKU: {candidate.sku || '-'}</s-text>
                                            </s-stack>
                                            <s-text color="subdued">{formatFitmentCountLabel(candidate.fitmentCount)}</s-text>
                                            <s-icon type="chevron-right" color="subdued" />
                                        </s-grid>
                                    </s-clickable>
                                ))}

                                {!copySearching && copyQuery.trim() && copySourceProducts.length === 0 && (
                                    <s-text color="subdued">No matching products found.</s-text>
                                )}
                            </s-stack>
                        </s-stack>
                    )}
                    {copySourceSelection && <>
                        <s-button slot="primary-action" variant="primary" tone={copyTargetFitmentCount > 0 ? 'critical' : 'auto'}
                            onClick={onConfirmCopyFitments} loading={submitting} disabled={loading || copyJobRunning}>
                            {copyTargetFitmentCount > 0 ? 'Replace fitments' : 'Copy fitments'}
                        </s-button>
                        <s-button slot="secondary-actions" onClick={() => setCopySourceSelection(null)} disabled={submitting || loading}>Back</s-button>
                        <s-button slot="secondary-actions" onClick={onCloseCopyModal} disabled={submitting || loading}>Cancel</s-button>
                    </>}
                </s-modal>

                <s-modal ref={scopeModalRef} id="pf-scope-action-modal"
                    heading={scopeAction?.operation === 'clear' ? 'Remove all fitments' : scopeAction?.operation === 'universal' ? 'Mark product as universal' : 'Turn off universal status'}>
                    {scopeAction && <s-paragraph>{scopeAction.operation === 'specific'
                        ? 'Universal fallback will be turned off. Existing fitment assignments will be kept.'
                        : scopeAction.operation === 'universal'
                            ? 'Existing fitment assignments will be kept and shown as Exact Fit. Other vehicles will show Universal Fit; check product specifications for compatibility.'
                            : `This will remove all ${scopeAction.count.toLocaleString()} assignments in the selected product or variant. Universal status will be kept. This action saves immediately.`}</s-paragraph>}
                    <s-button slot="primary-action" variant="primary" tone={scopeAction?.operation === 'clear' ? 'critical' : 'auto'}
                        loading={submitting}
                        onClick={() => scopeAction && submitChanges(saveAttempt ?? {
                            mutationId: crypto.randomUUID(), variantId: product?.selectedVariantId, changes: [],
                            operation: scopeAction.operation, scopeVersion: scopeAction.version,
                        })}>
                        {saveAttempt ? 'Retry' : 'Confirm'}
                    </s-button>
                    <s-button slot="secondary-actions" disabled={submitting || !!saveAttempt} onClick={() => setScopeAction(null)}>Cancel</s-button>
                </s-modal>

                <s-modal ref={blockerModalRef} id="pf-leave-product-modal" heading="Unsaved fitment changes">
                    <s-paragraph>Save or discard your changes before leaving this product.</s-paragraph>
                    <s-button slot="primary-action" variant="primary" onClick={() => blocker.state === 'blocked' && blocker.reset()}>Keep editing</s-button>
                    <s-button slot="secondary-actions" tone="critical" disabled={!!saveAttempt || !!syncRecoveryId}
                        onClick={() => { if (blocker.state === 'blocked') { receiveDraft(new FitmentDraft()); blocker.proceed(); } }}>
                        Discard and leave
                    </s-button>
                </s-modal>

                <s-modal ref={reviewModalRef} id="pf-review-changes-modal" heading="Unsaved fitment changes">
                    {/* s-modal keeps its children in the DOM; render the review only while open. */}
                    {!reviewChanges ? null : pendingFitmentRows.length === 0 ? <s-paragraph color="subdued">No unsaved changes.</s-paragraph> : (
                        <s-stack gap="base">
                            <s-text color="subdued">{pendingFitmentSummary}</s-text>
                            {pendingFitmentRows.map((row) => {
                                const keys = row.fitments.map(fitmentKey);
                                const changes = keys.map((key) => editDraft.changes.get(key)!);
                                const title = productDetailsFitmentFields.map((field) => row.fieldValues[normalizeFitmentKey(field)]).filter(Boolean).join(' · ');
                                const actions = [
                                    { label: 'Add fitment', changes: changes.filter((change) => !change.before) },
                                    { label: 'Remove fitment', changes: changes.filter((change) => !change.after) },
                                    { label: 'Update notes', changes: changes.filter((change) => change.before && change.after) },
                                ].filter((action) => action.changes.length > 0);
                                const notes = changes.find((change) => change.after)?.after?.notes ?? [];
                                return (
                                    <s-box key={row.key} border="base" borderRadius="base" padding="small" accessibilityLabel={title}>
                                        <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="start">
                                            <s-stack gap="small-300">
                                                <s-text fontWeight="medium">{title}</s-text>
                                                {actions.map((action) => (
                                                    <s-text color="subdued" key={action.label}>
                                                        {action.label} · {formatFitmentCountLabel(action.changes.length)}
                                                        {actions.length > 1 && standardYearFitmentField && ` (${formatYearRange(action.changes.map((change) => getFitmentDisplayValue((change.after ?? change.before)!, standardYearFitmentField)))})`}
                                                    </s-text>
                                                ))}
                                                {notes.map((note, index) => <s-text key={index}>{note.text}</s-text>)}
                                                {notes.length === 0 && actions.some((action) => action.label === 'Update notes') && <s-text>All notes removed.</s-text>}
                                            </s-stack>
                                            <s-button disabled={editingBlocked} accessibilityLabel={`Undo changes to ${title}`} onClick={() => receiveDraft(editDraftRef.current.undo(keys))}>Undo</s-button>
                                        </s-grid>
                                    </s-box>
                                );
                            })}
                        </s-stack>
                    )}
                    <s-button slot="primary-action" variant="primary" onClick={() => setReviewChanges(false)}>Done</s-button>
                </s-modal>

                <s-modal ref={editModalRef} id="pf-edit-fitment-modal" heading="Edit fitment">
                    {editingFitment && (
                        <s-stack gap="large">
                            {fitmentEditError && (
                                <s-banner heading="Cannot apply fitment changes" tone="critical">
                                    {fitmentEditError}
                                </s-banner>
                            )}

                            {editableFitmentFields.length > 0 && (
                                <s-stack gap="small-200" accessibilityLabel="Vehicle details">
                                    <s-heading>Vehicle details</s-heading>
                                    <s-grid gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))" gap="small-200">
                                        {editableFitmentFields.map((field) => {
                                            const key = normalizeFitmentKey(field);
                                            return (
                                                <Combobox
                                                    key={key}
                                                    label={field.label}
                                                    value={getFitmentFieldValue(editingFitment.fieldValues, field)}
                                                    options={editFieldOptions[key] ?? []}
                                                    disabled={editingBlocked || isFitmentFieldDisabled(editableFitmentFields, editingFitment.fieldValues, field, false)}
                                                    loading={loadingEditFieldOptions[key]}
                                                    placeholder={getFitmentFieldEmptyOptionLabel(field, 'select')}
                                                    onChange={(value, immediate) => onUpdateEditedFitmentField(field, value, immediate)}
                                                />
                                            );
                                        })}
                                    </s-grid>
                                </s-stack>
                            )}

                            {standardYearFitmentField && (
                                <s-stack gap="small-200" accessibilityLabel="Years">
                                    <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                                        <s-heading>Years</s-heading>
                                        <s-button variant="tertiary" icon="plus" onClick={onAddYearGroupRange} disabled={editingBlocked}>Add range</s-button>
                                    </s-stack>
                                    {editingFitment.ranges.map((range) => (
                                        <s-grid key={range.id} gridTemplateColumns={editingFitment.ranges.length > 1 ? '1fr 1fr auto' : '1fr 1fr'} gap="small-200" alignItems="end">
                                            {(['start', 'end'] as const).map((field) => (
                                                // display: contents keeps the field a direct grid item while
                                                // catching arrow keys from inside the field.
                                                <div key={field} style={{ display: 'contents' }} onKeyDown={(event) => stepYearRange(event, range, field)}>
                                                    <s-number-field
                                                        label={field === 'start' ? 'Start year' : 'End year'}
                                                        value={range[field]}
                                                        disabled={editingBlocked}
                                                        onInput={(event) => onUpdateYearGroupRange(range.id, field, fieldValue(event))}
                                                        inputMode="numeric"
                                                        min={1900}
                                                        max={2099}
                                                        step={1}
                                                    />
                                                </div>
                                            ))}
                                            {editingFitment.ranges.length > 1 && <s-button
                                                variant="tertiary"
                                                tone="critical"
                                                icon="delete"
                                                onClick={() => onRemoveYearGroupRange(range.id)}
                                                disabled={editingBlocked}
                                                accessibilityLabel={`Remove year range ${editingFitment.ranges.indexOf(range) + 1}`}
                                            />}
                                        </s-grid>
                                    ))}
                                </s-stack>
                            )}
                            <s-box ref={fitmentNotesRef} accessibilityLabel="Notes">
                                <s-stack gap="small-200">
                                    <FitmentNotesEditor
                                        label="Notes"
                                        noteLines={editingFitment.noteLines}
                                        onChange={(noteLines) => setEditingFitment((current) => current ? { ...current, noteLines } : current)}
                                        disabled={editingBlocked}
                                    />
                                    {standardYearFitmentField && <s-text color="subdued">Notes apply to all years selected above.</s-text>}
                                </s-stack>
                            </s-box>
                            <s-text color="subdued">Apply here, then save on the product page.</s-text>
                        </s-stack>
                    )}
                    <s-button slot="primary-action" variant="primary" onClick={onApplyFitmentEdit} disabled={editingBlocked || !editingFitment}>
                        Apply changes
                    </s-button>
                    <s-button slot="secondary-actions" onClick={closeFitmentEditor} disabled={submitting || loading}>Cancel</s-button>
                </s-modal>

                <VariantReviewDialog review={variantReview?.review ?? null} currentVariantId={selectedVariantId}
                    busy={submitting} retry={!!saveAttempt} error={error}
                    onClose={() => setVariantReview(null)}
                    onExclude={ids => void excludeConflictingVariants(ids)}
                    onSave={() => variantReview && void submitChanges(saveAttempt ?? variantReview.attempt)} />

                <s-stack gap="base">
                <s-heading fontSize="large-200">{product?.title || 'Product Details'}</s-heading>
                {error && <s-banner heading="Error" tone="critical">{error}</s-banner>}

                <ProductSummaryCard product={product} isUniversal={draftIsUniversal} adminUrl={shopifyAdminProductUrl}
                    status={product?.status?.trim() ? productStatusLabel(product.status) : undefined}>
                    {showFitmentScopeSelector && (
                        <s-stack direction="inline" gap="base" alignItems="end">
                            <s-box minInlineSize="0">
                                <s-select
                                    label="Fitments for"
                                    value={selectedScopeValue}
                                    disabled={loading || submitting || hasUnsavedChanges || copyJobRunning}
                                    onChange={(event) => {
                                        setError('');
                                        setAssignedFitmentSearch('');
                                        setDebouncedFitmentSearch('');
                                        setEditingFitment(null);
                                        setFitmentEditError('');
                                        void loadDetails(fieldValue(event));
                                    }}
                                >
                                    {fitmentScopeOptions.map((option) => 'options' in option ? (
                                        <s-option-group key={option.title} label={option.title}>
                                            {(option.options ?? []).map((variantOption) => (
                                                <s-option key={variantOption.value} value={variantOption.value}>{variantOption.label}</s-option>
                                            ))}
                                        </s-option-group>
                                    ) : (
                                        <s-option key={option.value} value={option.value}>{option.label}</s-option>
                                    ))}
                                </s-select>
                            </s-box>
                            {isVariantScope && (product?.variants.length ?? 0) > 1 && (
                                <s-stack direction="inline" gap="small-200" alignItems="center">
                                    <s-checkbox label={targetVariantIds.length && targetVariantIds.length < (product?.variants.length ?? 0) - 1
                                        ? `Apply to ${targetVariantIds.length + 1} variants` : 'Apply to all variants'}
                                        checked={targetVariantIds.length > 0} disabled={editingBlocked}
                                        onChange={event => setTargetVariantIds(fieldChecked(event) ? (product?.variants ?? []).filter(v => v.id !== selectedVariantId).map(v => v.id) : [])} />
                                    <VariantTargetPicker open={variantPickerOpen} variants={product?.variants ?? []}
                                        currentVariantId={selectedVariantId} selected={targetVariantIds} disabled={editingBlocked}
                                        onToggle={() => setVariantPickerOpen(open => !open)} onClose={() => setVariantPickerOpen(false)}
                                        onSelect={ids => { setTargetVariantIds(ids); setVariantReview(null); }} />
                                    <s-button variant="tertiary" icon="question-circle" accessibilityLabel="How variant edits apply"
                                        interestFor="pf-variant-edits-tip" />
                                    <s-tooltip id="pf-variant-edits-tip">
                                        For this save only: add missing fitments and edit or remove matching fitments. Existing differences are preserved. Copy and Remove all affect only the current variant.
                                    </s-tooltip>
                                    {targetVariantIds.length > 100 && <s-text tone="critical">Choose up to 100 other variants.</s-text>}
                                </s-stack>
                            )}
                        </s-stack>
                    )}
                </ProductSummaryCard>

                <s-section accessibilityLabel="Fitments">
                    <s-menu id={fitmentActionsMenuId} accessibilityLabel="More fitment actions">
                        <s-button disabled={copyJobRunning || hasUnsavedChanges} onClick={onOpenCopyModal}>Copy fitments from product</s-button>
                        <s-button onClick={() => setCopyHistoryOpen(true)}>View copy history</s-button>
                        <s-button disabled={editingBlocked} onClick={() => setShowRawTags((current) => !current)}>
                            {showRawTags ? 'Hide tags' : 'Show tags'}
                        </s-button>
                        <s-button tone="critical" icon="delete"
                            disabled={(product?.fitmentCount ?? 0) === 0 || editingBlocked || hasUnsavedChanges}
                            onClick={() => { void prepareScopeAction('clear'); }}>
                            Remove all fitments
                        </s-button>
                    </s-menu>

                    <s-stack gap="base">
                        {/* Actions live in the body: a section action slot does not open the menu. */}
                        <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
                            <s-stack direction="inline" gap="large" alignItems="center">
                                <s-heading>Fitments</s-heading>
                                {!isVariantScope && (
                                    <s-stack direction="inline" gap="small-300" alignItems="center">
                                        <s-checkbox label="Universal product" checked={draftIsUniversal}
                                            onChange={(event) => onToggleUniversal(fieldChecked(event))} disabled={editingBlocked || hasUnsavedChanges} />
                                        <s-button variant="tertiary" icon="question-circle" accessibilityLabel="About universal products"
                                            interestFor="pf-universal-tip" />
                                        <s-tooltip id="pf-universal-tip">
                                            Assigned vehicles show Exact Fit. Other vehicles show Universal Fit; check product specifications for compatibility.
                                        </s-tooltip>
                                    </s-stack>
                                )}
                            </s-stack>
                            <s-button icon="chevron-down" commandFor={fitmentActionsMenuId} disabled={submitting || loading}>
                                More actions
                            </s-button>
                        </s-grid>
                        {hasUnsavedChanges && (
                            <s-banner tone="info">
                                {syncRecoveryId ? 'Fitment changes saved; Shopify synchronization pending.' : pendingFitmentSummary}
                                {editDraft.changes.size >= 400 ? ` · Save or discard before adding more changes (limit ${FITMENT_CHANGE_LIMIT}).` : ''}
                                {(saveAttempt || syncRecoveryId) && <s-paragraph>Retry Save to confirm the previous attempt and finish Shopify synchronization.</s-paragraph>}
                                {editDraft.changes.size > 0 && (
                                    <s-button slot="secondary-actions" onClick={() => setReviewChanges(true)}>Review changes</s-button>
                                )}
                            </s-banner>
                        )}

                        {activeCopyJob && (
                            <s-banner heading="Copying fitments" tone="info">
                                <s-stack gap="small-200">
                                    <s-paragraph>From {activeCopyJob.sourceProductTitle}</s-paragraph>
                                    <s-progress accessibilityLabel="Copy progress" value={activeCopyJob.progressPercent} max={100} />
                                    <s-paragraph>{activeCopyJob.message}</s-paragraph>
                                    <s-paragraph color="subdued">You can leave this page. The copy will continue in the background.</s-paragraph>
                                </s-stack>
                            </s-banner>
                        )}

                        {latestFailedCopyJob && (
                            <s-banner heading="Fitment copy failed" tone="critical">
                                {latestFailedCopyJob.message}
                                <s-button
                                    slot="secondary-actions"
                                    onClick={() => onRetryCopyJob(latestFailedCopyJob)}
                                    disabled={submitting || loading || copyJobRunning || hasUnsavedChanges}
                                >
                                    Retry copy
                                </s-button>
                            </s-banner>
                        )}

                        {fitmentSelectorConfigured && (
                            <s-stack gap="small-200">
                                <s-query-container>
                                    <s-grid gridTemplateColumns={addFitmentGridColumns} gap="small-200" alignItems="end">
                                        {productDetailsFitmentFields.map((field) => {
                                            const key = normalizeFitmentKey(field);
                                            const isYearField = field === standardYearFitmentField;
                                            const fieldDisabled = editingBlocked || isFitmentFieldDisabled(productDetailsFitmentFields, selectedFitmentValues, field, false);
                                            return (
                                                <React.Fragment key={key}>
                                                    <Combobox
                                                        label={field.label}
                                                        options={fieldOptions[key] ?? [{ label: getFitmentFieldEmptyOptionLabel(field, 'select'), value: '' }]}
                                                        value={getFitmentFieldValue(selectedFitmentValues, field)}
                                                        disabled={fieldDisabled}
                                                        loading={loadingFieldOptions[key]}
                                                        placeholder={getFitmentFieldEmptyOptionLabel(field, 'select')}
                                                        onChange={(value, immediate) => {
                                                            setOptionsDelay(immediate ? 0 : 200);
                                                            setSelectedFitmentValues((current) => updateFitmentFieldValue(productDetailsFitmentFields, current, field, value));
                                                        }}
                                                    />
                                                    {isYearField && (
                                                        <Combobox
                                                            label="Year to"
                                                            options={fieldOptions[key] ?? []}
                                                            value={selectedFitmentYearTo}
                                                            disabled={fieldDisabled || !selectedFitmentYearValue}
                                                            loading={loadingFieldOptions[key]}
                                                            placeholder="Select year to"
                                                            error={addFitmentYearRange.error}
                                                            onChange={setSelectedFitmentYearTo}
                                                        />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </s-grid>
                                </s-query-container>
                                <s-stack direction="inline" gap="small-200" alignItems="center" justifyContent="end">
                                    <s-button variant="tertiary" icon={showNewFitmentNotes ? undefined : 'plus'} onClick={() => {
                                        setShowNewFitmentNotes((current) => !current);
                                        if (!showNewFitmentNotes) setNewFitmentNoteLines((current) => current.length > 0 ? current : ['']);
                                    }} disabled={editingBlocked}>
                                        {showNewFitmentNotes ? 'Hide notes' : 'Add notes'}
                                    </s-button>
                                    <s-button variant="primary" onClick={onAddFitment} disabled={!canAddFitment || editingBlocked}>Add fitment</s-button>
                                </s-stack>
                                {showNewFitmentNotes && (
                                    <FitmentNotesEditor
                                        label="Notes"
                                        noteLines={newFitmentNoteLines}
                                        onChange={setNewFitmentNoteLines}
                                        disabled={editingBlocked}
                                        autoFocus
                                    />
                                )}
                            </s-stack>
                        )}

                        <s-divider />

                        <s-stack gap="small-200">
                            {showAssignedFitmentToolbar && (
                                <s-query-container>
                                    <s-grid gridTemplateColumns={standardYearFitmentField ? '1fr auto' : '1fr'} gap="small-200" alignItems="center">
                                        <s-search-field
                                            label="Search assigned fitments"
                                            labelAccessibilityVisibility="exclusive"
                                            value={assignedFitmentSearch}
                                            onInput={(event) => setAssignedFitmentSearch(fieldValue(event))}
                                            placeholder="Search fitments or notes"
                                        />
                                        {standardYearFitmentField && (
                                            <s-select label="Assigned fitment view" labelAccessibilityVisibility="exclusive"
                                                value={effectiveAssignedFitmentView}
                                                onChange={(event) => setAssignedFitmentView(fieldValue(event) as AssignedFitmentView)}>
                                                <s-option value="grouped">Year ranges</s-option>
                                                <s-option value="individual">Individual years</s-option>
                                            </s-select>
                                        )}
                                    </s-grid>
                                </s-query-container>
                            )}
                            {assignedFitmentPageError && (
                                <s-banner tone="critical" heading="Unable to load fitments">
                                    {assignedFitmentPageError}
                                    <s-button slot="secondary-actions" onClick={() => { void loadAssignedFitmentPage(requestedFitmentPageRef.current); }}>
                                        Retry loading fitments
                                    </s-button>
                                </s-banner>
                            )}
                            {assignedFitmentRows.length > 0 ? (
                                <s-table
                                    ref={assignedTableEvents}
                                    loading={assignedFitmentPageLoading}
                                    paginate={assignedFitmentTotalPages > 1}
                                    hasPreviousPage={!assignedFitmentPageLoading && !!product?.fitmentHasPrevious}
                                    hasNextPage={!assignedFitmentPageLoading && !!product?.fitmentHasNext}
                                >
                                    <s-table-header-row>
                                        {productDetailsFitmentFields.map((field, index) => (
                                            <s-table-header key={field.fieldIndex} listSlot={index === 0 ? 'primary' : 'labeled'}>{field.label}</s-table-header>
                                        ))}
                                        <s-table-header>Notes</s-table-header>
                                        {showRawTags && <s-table-header>Tag</s-table-header>}
                                        <s-table-header />
                                    </s-table-header-row>
                                    <s-table-body>
                                        {assignedFitmentRows.map((row) => {
                                            const rowFitmentKeys = row.fitments.map(getFitmentIdentity);
                                            const notesFitment = row.fitments[0];
                                            const noteLines = getFitmentNoteLines(notesFitment);
                                            const notesText = getFitmentNotesText(notesFitment);
                                            const edited = row.fitments.some(fitment => editDraft.changes.has(fitmentKey(fitment)));
                                            return (
                                                <s-table-row key={row.key}>
                                                    {productDetailsFitmentFields.map((field) => (
                                                        <s-table-cell key={`${row.key}-${field.fieldIndex}`}>
                                                            {row.fieldValues[normalizeFitmentKey(field)]}
                                                        </s-table-cell>
                                                    ))}
                                                    <s-table-cell>
                                                        {notesText ? (
                                                            <s-stack direction="inline" gap="small-200" alignItems="center">
                                                                <s-button variant="tertiary" onClick={() => onOpenFitmentEditor(row, true)} disabled={editingBlocked}
                                                                    interestFor={`pf-notes-${row.key}`}>
                                                                    {noteLines[0]}
                                                                </s-button>
                                                                {noteLines.length > 1 && <s-tooltip id={`pf-notes-${row.key}`}>{notesText}</s-tooltip>}
                                                                {noteLines.length > 1 && <s-text color="subdued">+{noteLines.length - 1} more</s-text>}
                                                            </s-stack>
                                                        ) : (
                                                            <s-button variant="tertiary" icon="plus" onClick={() => onOpenFitmentEditor(row, true)} disabled={editingBlocked}>Add notes</s-button>
                                                        )}
                                                    </s-table-cell>
                                                    {showRawTags && (
                                                        <s-table-cell>
                                                            {row.fitments.length === 1 ? row.fitments[0].tag || '-' : `${row.fitments.length} tags`}
                                                        </s-table-cell>
                                                    )}
                                                    <s-table-cell>
                                                        <s-stack direction="inline" gap="small-200" justifyContent="end" alignItems="center">
                                                            {edited && <s-badge tone="info">Unsaved</s-badge>}
                                                            {visibleFitmentFields.length > 0 && (
                                                                <s-button variant="tertiary" onClick={() => onOpenFitmentEditor(row)} disabled={editingBlocked}>
                                                                    Edit
                                                                </s-button>
                                                            )}
                                                            <s-button icon="delete" variant="tertiary" tone="critical" onClick={() => onRemoveFitments(rowFitmentKeys, row.key)} disabled={editingBlocked}>
                                                                Remove
                                                            </s-button>
                                                        </s-stack>
                                                    </s-table-cell>
                                                </s-table-row>
                                            );
                                        })}
                                    </s-table-body>
                                </s-table>
                            ) : isAssignedFitmentsLoading ? (
                                <s-stack direction="inline" gap="small-200" alignItems="center" accessibilityRole="status">
                                    <s-spinner accessibilityLabel="Loading assigned fitments" size="base" />
                                    <s-text color="subdued">Loading assigned fitments...</s-text>
                                </s-stack>
                            ) : assignedFitmentPageError ? null : (
                                <s-text color="subdued">
                                    {hasAssignedFitmentRefinements
                                        ? 'No fitments match the current filters.'
                                        : (product?.fitmentCount ?? 0) > 0 ? 'No fitments to display on this page. Review your changes or choose another page.' : 'No fitments yet. Add a vehicle above.'}
                                </s-text>
                            )}
                            {assignedFitmentTotalPages > 1 && (
                                <s-text color="subdued">Page {assignedFitmentCurrentPage} of {assignedFitmentTotalPages}</s-text>
                            )}
                        </s-stack>

                        {!isVariantScope && importableDetectedFitments.length > 0 && (
                            <>
                                <s-divider />
                                <s-stack gap="small-200">
                                    <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="base">
                                        <s-stack gap="none">
                                            <s-heading>Detected fitments</s-heading>
                                            <s-text color="subdued">Potential vehicle tags detected from the product&apos;s current Shopify tags.</s-text>
                                        </s-stack>
                                        {/* Import converts detected tags into the shop's current canonical tag format, but still only stages them locally until Save. */}
                                        <s-button onClick={onImportDetectedFitments} disabled={editingBlocked}>
                                            Import detected fitments
                                        </s-button>
                                    </s-stack>
                                    <s-table>
                                        <s-table-header-row>
                                            {productDetailsFitmentFields.map((field, index) => (
                                                <s-table-header key={field.fieldIndex} listSlot={index === 0 ? 'primary' : 'labeled'}>{field.label}</s-table-header>
                                            ))}
                                            <s-table-header>Detected tag</s-table-header>
                                            <s-table-header>Will import as</s-table-header>
                                        </s-table-header-row>
                                        <s-table-body>
                                            {importableDetectedFitments.map((fitment) => (
                                                <s-table-row key={fitment.canonicalTag}>
                                                    {productDetailsFitmentFields.map((field) => (
                                                        <s-table-cell key={`${fitment.canonicalTag}-${field.fieldIndex}`}>
                                                            {getFitmentDisplayValue(fitment, field)}
                                                        </s-table-cell>
                                                    ))}
                                                    <s-table-cell>{fitment.sourceTag}</s-table-cell>
                                                    <s-table-cell>{fitment.canonicalTag}</s-table-cell>
                                                </s-table-row>
                                            ))}
                                        </s-table-body>
                                    </s-table>
                                </s-stack>
                            </>
                        )}
                    </s-stack>
                </s-section>
                <RecentCopyActivity key={productId} jobs={copyJobs} variants={product?.variants} onRetry={onRetryCopyJob}
                    disabled={submitting || loading || copyJobRunning || hasUnsavedChanges}
                    onViewAll={() => setCopyHistoryOpen(true)} />
                </s-stack>
            </s-page>
        </>
    );
};

export default observer(ProductDetailsPage);
